import { SdkContext } from '../internal'
import type { CommunityTheme, CommunityThemeList, ReadyMadeTheme, ThemeValues } from '@/lib/theme'

/** Partial values on write: what is left out stays as it is (the API completes it). */
export type ThemeValuesInput = Partial<Omit<ThemeValues, 'light' | 'dark'>> & {
  light?: Partial<ThemeValues['light']>
  dark?: Partial<ThemeValues['dark']>
}

/** F-191 phase 4b: a theme proposed from an image's colors (not saved). */
export interface ThemeFromImage {
  source: 'upload' | 'logo'
  /** The dominant colors extracted on the server, in hex (6 to 8). */
  colors: string[]
  values: ThemeValues
  /** The params the server darkened or lightened to reach AA. */
  adjusted: string[]
  warnings: unknown[]
}

/**
 * F-191 phase 4: the community's saved themes and the ready-made gallery
 * (owner and admin of the `X-CommunityId`). `id` is a theme id or `active`.
 */
export class CommunityThemesResource {
  constructor(private ctx: SdkContext) {}

  async presets() {
    return this.ctx.call<{ presets: ReadyMadeTheme[] }>({ httpMethod: 'GET', path: '/appearance/themes' })
  }

  async list() {
    return this.ctx.call<CommunityThemeList>({ httpMethod: 'GET', path: '/community-themes' })
  }

  async get(id: string) {
    return this.ctx.call<{ theme: CommunityTheme }>({ httpMethod: 'GET', path: '/community-themes/{id}' }, { id })
  }

  /** "Usar este tema" (`presetKey` + `activate`), "Personalizar" › Salvar, "Duplicar" (`sourceThemeId`). */
  async create(body: { name?: string; presetKey?: string; sourceThemeId?: string; values?: ThemeValuesInput; activate?: boolean; origin?: 'image' }) {
    return this.ctx.call<{ theme: CommunityTheme }>({ httpMethod: 'POST', path: '/community-themes', hasBody: true }, { body })
  }

  async update(id: string, body: { name?: string; values?: ThemeValuesInput }) {
    return this.ctx.call<{ theme: CommunityTheme }>({ httpMethod: 'PATCH', path: '/community-themes/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string) {
    return this.ctx.call<{ id: string }>({ httpMethod: 'DELETE', path: '/community-themes/{id}' }, { id })
  }

  async activate(id: string) {
    return this.ctx.call<{ changed: boolean; theme: CommunityTheme }>({ httpMethod: 'POST', path: '/community-themes/{id}/activate' }, { id })
  }

  async restore(id: string) {
    return this.ctx.call<{ theme: CommunityTheme }>({ httpMethod: 'POST', path: '/community-themes/{id}/restore' }, { id })
  }

  /**
   * Phase 4b, "Gerar a partir de uma imagem": an uploaded PNG, JPG or WebP (up
   * to 5 MB) or the community's logo. Uses 1 AI use of the plan; nothing is
   * saved (save with `create({ values, origin: 'image' })`).
   */
  async fromImage(input: File | 'logo') {
    if (input === 'logo') {
      return this.ctx.call<ThemeFromImage>({ httpMethod: 'POST', path: '/communities/appearance/theme-from-image', hasBody: true }, { body: { source: 'logo' } })
    }
    const fd = new FormData()
    fd.append('file', input)
    return this.ctx.call<ThemeFromImage>({ httpMethod: 'POST', path: '/communities/appearance/theme-from-image', hasBody: true }, { body: fd })
  }
}
